import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../services/api";
import StarRating from "../components/StarRating";
import VerifiedSellerBadge from "../components/VerifiedSellerBadge";
import { useAuth } from "../context/AuthContext";
import { addRecentlyViewed } from "../utils/history";
import FavoriteButton from "../components/FavoriteButton";
import { useCurrency } from "../context/CurrencyContext";
import { formatPrice } from "../utils/formatPrice";
import { addCartItem } from "../utils/cart";
import { useNotification } from "../context/NotificationContext";
import Loader from "../components/Loader";
import ProductCard from "../components/ProductCard";
import {
  localizedCategoryName,
  localizedProductDescription,
  localizedProductName,
  localizedProductExtraInformation,
  pickLocalizedField,
} from "../utils/i18nContent";
import {
  FiStar,
  FiEdit,
  FiTruck,
  FiShield,
  FiMinus,
  FiPlus,
  FiMessageCircle,
  FiEye,
  FiShoppingBag,
  FiCheckCircle,
  FiAlertCircle,
  FiPlay,
  FiZap,
  FiChevronRight,
  FiChevronLeft,
  FiArrowLeft,
  FiMaximize2,
  FiX,
  FiShare2,
  FiCheck,
  FiAward,
  FiTag,
  FiCalendar,
} from "react-icons/fi";
import { formatProductAddedDate, formatProductFullDate } from "../utils/dateFormatter";
import { formatStockDisplay } from "../utils/stockFormatter";

// ── COLOR MAP FOR SWATCHES ──
const COLOR_MAP = {
  black: "#111111",
  noir: "#111111",
  white: "#ffffff",
  blanc: "#ffffff",
  red: "#ef4444",
  rouge: "#ef4444",
  blue: "#3b82f6",
  bleu: "#3b82f6",
  green: "#22c55e",
  vert: "#22c55e",
  yellow: "#eab308",
  jaune: "#eab308",
  orange: "#ff6a00",
  purple: "#a855f7",
  violet: "#a855f7",
  pink: "#ec4899",
  rose: "#ec4899",
  brown: "#854d0e",
  marron: "#854d0e",
  beige: "#d4b996",
  grey: "#6b7280",
  gray: "#6b7280",
  gris: "#6b7280",
  gold: "#eab308",
  or: "#eab308",
  silver: "#94a3b8",
  argent: "#94a3b8",
  navy: "#1e3a8a",
  marine: "#1e3a8a",
  tan: "#d2b48c",
  khaki: "#c3b091",
};

// ── SUB-COMPONENT: ReviewForm (Apple Form Style) ──
const ReviewForm = ({ productId, onReviewSubmitted }) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      setError(t("productDetail.ratingRequiredError", "Please select a rating before submitting."));
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.post(`/products/${productId}/reviews/`, { rating, comment });
      setRating(0);
      setComment("");
      onReviewSubmitted();
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        t("productDetail.alreadyReviewedError", "You have already reviewed this product.");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-[20px] bg-[var(--color-bg)] border border-[var(--color-border)] p-5 sm:p-6 mb-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h5 className="text-base font-semibold text-[var(--color-text-primary)] tracking-tight">
          {t("productDetail.writeReview", "Write a Customer Review")}
        </h5>
        <span className="text-[11px] font-medium text-[var(--color-text-secondary)]">
          {t("common.requiredFields", "* Required")}
        </span>
      </div>

      {error && (
        <div className="bg-red-500/10 text-red-600 dark:text-red-400 text-xs font-semibold px-4 py-2.5 rounded-xl mb-4 flex items-center gap-2 border border-red-500/20">
          <FiAlertCircle size={15} className="shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-[11px] font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider mb-2">
            {t("productDetail.yourRating", "Your Rating")} *
          </label>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => {
              const active = star <= (hoverRating || rating);
              return (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className={`w-10 h-10 rounded-xl transition-all flex items-center justify-center ${
                    active
                      ? "bg-[var(--color-accent)] text-white shadow-md shadow-orange-500/20 scale-105"
                      : "bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-tertiary)] hover:border-[var(--color-accent)]"
                  }`}
                  style={
                    active
                      ? { background: "linear-gradient(135deg, #ff9f0a 0%, #ff5e00 100%)" }
                      : undefined
                  }
                  aria-label={`Rate ${star} star`}
                >
                  <FiStar size={18} fill={active ? "currentColor" : "none"} />
                </button>
              );
            })}
            <span className="ml-2 text-xs font-semibold text-[var(--color-text-primary)]">
              {(hoverRating || rating) > 0 ? `${hoverRating || rating} / 5` : ""}
            </span>
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider mb-2">
            {t("productDetail.yourComment", "Your Feedback")}
          </label>
          <textarea
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t(
              "productDetail.commentPlaceholder",
              "How was the quality, packaging, and delivery speed? Share your experience with other buyers..."
            )}
            className="w-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-[14px] p-3.5 text-xs sm:text-sm font-medium text-[var(--color-text-primary)] placeholder-[var(--color-text-tertiary)] focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20 outline-none transition-all resize-none"
          />
        </div>

        <button
          type="submit"
          disabled={loading || rating === 0}
          className="btn-primary px-6 h-10 text-xs font-semibold disabled:opacity-50 disabled:pointer-events-none"
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
          ) : (
            <FiCheck size={14} className="mr-1.5" />
          )}
          {t("productDetail.submitReview", "Submit Review")}
        </button>
      </form>
    </div>
  );
};

// ── SUB-COMPONENT: ReviewItem (Clean Apple Review Card) ──
const ReviewItem = ({ review, onReviewUpdated }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editedComment, setEditedComment] = useState(review.comment);
  const [editLoading, setEditLoading] = useState(false);

  const canEdit = user && user.id === review.reviewer?.id;
  const reviewerProfile =
    review.reviewer?.user_type === "SUPPLIER"
      ? review.reviewer?.supplier_profile
      : review.reviewer?.buyer_profile;
  const reviewerName =
    reviewerProfile?.company_name ||
    reviewerProfile?.name ||
    review.reviewer?.first_name ||
    t("common.verifiedBuyer", "Verified Buyer");
  const reviewerAvatar =
    reviewerProfile?.logo_url ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(reviewerName)}&background=f5f5f7&color=ff6a00&bold=true`;

  const handleSave = async () => {
    setEditLoading(true);
    try {
      await api.patch(`/products/${review.product}/reviews/${review.id}/`, {
        comment: editedComment,
      });
      setIsEditing(false);
      onReviewUpdated();
    } catch (error) {
      console.error("Failed to update review", error);
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-5 rounded-[18px] bg-[var(--color-bg)] border border-[var(--color-border)] transition-all">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <img
            src={reviewerAvatar}
            alt={reviewerName}
            className="w-10 h-10 rounded-full object-cover bg-[var(--color-bg-secondary)] border border-[var(--color-border)] shadow-sm shrink-0"
          />
          <div>
            <div className="flex items-center gap-2">
              <h6 className="font-semibold text-[var(--color-text-primary)] text-xs sm:text-sm tracking-tight">
                {reviewerName}
              </h6>
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-full">
                <FiCheckCircle size={10} /> {t("common.verified", "Verified")}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <StarRating rating={review.rating} size={12} />
              <span className="text-[10px] font-medium text-[var(--color-text-tertiary)]">
                {new Date(review.created_at).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>
        </div>

        {canEdit && !isEditing && (
          <button
            type="button"
            className="p-1.5 rounded-full text-[var(--color-text-tertiary)] hover:text-[var(--color-accent)] hover:bg-[var(--color-bg-secondary)] transition-colors"
            onClick={() => setIsEditing(true)}
            title={t("common.edit", "Edit Review")}
          >
            <FiEdit size={14} />
          </button>
        )}
      </div>

      {!isEditing ? (
        <p className="text-[var(--color-text-primary)] text-xs sm:text-sm font-normal leading-relaxed mt-3 whitespace-pre-line pl-0.5">
          {review.comment}
        </p>
      ) : (
        <div className="space-y-2.5 mt-3">
          <textarea
            value={editedComment}
            onChange={(e) => setEditedComment(e.target.value)}
            rows={3}
            className="w-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-[14px] p-3 text-xs sm:text-sm font-medium text-[var(--color-text-primary)] focus:border-[var(--color-accent)] outline-none"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="px-3 py-1.5 text-xs font-semibold text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
            >
              {t("common.cancel", "Cancel")}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={editLoading}
              className="btn-primary px-4 h-8 text-xs font-semibold"
            >
              {editLoading ? (
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t("common.save", "Save")
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── MAIN COMPONENT: ProductDetailPage ──
const ProductDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { currency, rates } = useCurrency();
  const relatedScrollRef = useRef(null);

  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [userHasReviewed, setUserHasReviewed] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedMedia, setSelectedMedia] = useState("");
  const [selectedMediaType, setSelectedMediaType] = useState("image");
  const [selectedOrderSize, setSelectedOrderSize] = useState(1);
  const [selectedSelections, setSelectedSelections] = useState({});
  const [activeTab, setActiveTab] = useState("description");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const fetchProductData = useCallback(async () => {
    setLoading(true);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
    try {
      try {
        await api.post(`/products/${id}/increment_view/`);
      } catch (incrementError) {
        console.warn(
          "Failed to increment view count:",
          incrementError.response?.data || incrementError.message
        );
      }

      const productRes = await api.get(`/products/${id}/`);
      const productData = productRes.data;
      setProduct(productData);
      setSelectedOrderSize(Math.max(productData.minimum_order_quantity || 1, 1));
      addRecentlyViewed(productData);

      if (productData.thumbnail_url) {
        setSelectedMedia(productData.thumbnail_url);
        setSelectedMediaType("image");
      } else if (productData.images && productData.images.length > 0) {
        const firstImageUrl =
          productData.images[0].image_url || productData.images[0].media_url;
        setSelectedMedia(firstImageUrl);
        setSelectedMediaType(productData.images[0].media_type || "image");
      }

      const categoryId = productData.category?.id;
      const [reviewsRes, relatedRes] = await Promise.all([
        api.get(`/products/${id}/reviews/`),
        categoryId
          ? api.get(`/products/?category=${categoryId}&exclude=${id}`)
          : Promise.resolve({ data: { results: [] } }),
      ]);
      setReviews(reviewsRes.data.results || reviewsRes.data || []);
      setRelatedProducts(relatedRes.data.results || relatedRes.data || []);
    } catch (err) {
      console.error("Failed to fetch product data", err);
      setError(t("productDetail.loadError", "Unable to load product details."));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchProductData();
  }, [fetchProductData]);

  useEffect(() => {
    if (user && reviews.length > 0) {
      setUserHasReviewed(reviews.some((r) => r.reviewer?.id === user.id));
    } else {
      setUserHasReviewed(false);
    }
  }, [reviews, user]);

  useEffect(() => {
    if (!product) return;
    const selections = Array.isArray(product.selections) ? product.selections : [];
    const defaults = {};
    selections.forEach((selection) => {
      if (
        selection &&
        typeof selection.name === "string" &&
        Array.isArray(selection.values) &&
        selection.values.length > 0
      ) {
        defaults[selection.name] = selection.values[0];
      }
    });
    setSelectedSelections(defaults);
  }, [product]);

  const handleSendMessage = async () => {
    if (!isAuthenticated) return navigate("/login");
    if (!product || !user) return;
    if (product.supplier?.id === user.id)
      return alert(t("productDetail.cannotMessageSelf", "You cannot send messages to yourself."));

    try {
      const response = await api.post("/conversations/", {
        product: product.id,
        participants: [product.supplier.id],
      });
      navigate(`/chat/${response.data.id}`);
    } catch (err) {
      alert(t("productDetail.sendMessageError", "Failed to start conversation with supplier."));
    }
  };

  const scrollRelated = (direction) => {
    if (relatedScrollRef.current) {
      relatedScrollRef.current.scrollBy({
        left: direction === "left" ? -350 : 350,
        behavior: "smooth",
      });
    }
  };

  const safeProduct = product || {};
  const seller = safeProduct.supplier || {};
  const sellerProfile = seller.supplier_profile || {};

  const productSelections = Array.isArray(safeProduct.selections)
    ? safeProduct.selections.filter(
        (selection) =>
          selection &&
          typeof selection.name === "string" &&
          selection.name.trim() &&
          Array.isArray(selection.values) &&
          selection.values.length > 0
      )
    : [];

  const selectedSelectionEntries = productSelections
    .map((selection) => ({
      name: selection.name,
      value: selectedSelections[selection.name] || selection.values?.[0] || "",
    }))
    .filter((selection) => selection.name && selection.value);

  const baseUnitPrice = Number(safeProduct.price || safeProduct.max_price || safeProduct.min_price || 0);
  const selectionPriceOverride = productSelections.reduce((override, selection) => {
    if (override !== null) return override;
    const selectedValue = selectedSelections[selection.name] || selection.values?.[0] || "";
    const raw = selection?.prices?.[selectedValue];
    if (raw === undefined || raw === null || raw === "") return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }, null);

  const resolvedUnitPrice = selectionPriceOverride ?? baseUnitPrice;
  const productPrice = formatPrice(resolvedUnitPrice, currency, rates);
  const productName = localizedProductName(safeProduct, i18n.language);
  const productDescription = localizedProductDescription(safeProduct, i18n.language);
  const productExtraInformation = localizedProductExtraInformation(safeProduct, i18n.language);
  const productCategoryName = localizedCategoryName(safeProduct.category, i18n.language);
  const reviewCount = reviews.length || safeProduct.reviews_count || 0;
  const stockInfo = formatStockDisplay(safeProduct.quantity_in_stock, i18n.language);
  const inStock = stockInfo.inStock;
  const addedDateText = formatProductAddedDate(safeProduct.created_at, i18n.language);
  const fullDateText = formatProductFullDate(safeProduct.created_at, i18n.language);
  const minOrderQty = safeProduct.minimum_order_quantity || 1;

  const handleAddToCart = () => {
    addCartItem(safeProduct, {
      quantity: selectedOrderSize,
      selectedSelections: selectedSelectionEntries,
      customPrice: resolvedUnitPrice,
    });
    showNotification(
      t("notifications.productAddedToCart", { name: productName }),
      "success"
    );
  };

  const handleBuyNow = () => {
    if (!isAuthenticated) return navigate("/login");
    navigate("/checkout", {
      state: {
        buyNowItem: {
          id: safeProduct.id,
          name: productName,
          thumbnail_url: safeProduct.thumbnail_url,
          min_price: resolvedUnitPrice,
          max_price: resolvedUnitPrice,
          price: resolvedUnitPrice,
          quantity: selectedOrderSize,
          selected_selections: selectedSelectionEntries,
          minimum_order_quantity: minOrderQty,
          supplier: safeProduct.supplier || null,
        },
      },
    });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: productName, url });
      } catch (err) {
        // User cancelled share
      }
    } else {
      await navigator.clipboard.writeText(url);
      setCopiedLink(true);
      showNotification(t("common.linkCopied", "Link copied to clipboard!"), "info");
      setTimeout(() => setCopiedLink(false), 2500);
    }
  };

  // Build unique media gallery
  const gallery = [];
  if (safeProduct.thumbnail_url) {
    gallery.push({ id: "thumb", media_url: safeProduct.thumbnail_url, media_type: "image" });
  }
  if (safeProduct.images) {
    safeProduct.images.forEach((img) =>
      gallery.push({
        ...img,
        media_url: img.image_url || img.media_url,
        media_type: img.media_type || "image",
      })
    );
  }
  const uniqueGallery = gallery.filter(
    (v, i, a) => a.findIndex((t) => t.media_url === v.media_url) === i
  );

  const currentMediaIndex = uniqueGallery.findIndex(
    (item) => item.media_url === selectedMedia
  );

  const handlePrevMedia = () => {
    if (uniqueGallery.length <= 1) return;
    const prevIdx = (currentMediaIndex - 1 + uniqueGallery.length) % uniqueGallery.length;
    setSelectedMedia(uniqueGallery[prevIdx].media_url);
    setSelectedMediaType(uniqueGallery[prevIdx].media_type || "image");
  };

  const handleNextMedia = () => {
    if (uniqueGallery.length <= 1) return;
    const nextIdx = (currentMediaIndex + 1) % uniqueGallery.length;
    setSelectedMedia(uniqueGallery[nextIdx].media_url);
    setSelectedMediaType(uniqueGallery[nextIdx].media_type || "image");
  };

  const galleryTouchStartX = useRef(0);
  const handleGalleryTouchStart = (e) => {
    galleryTouchStartX.current = e.touches[0].clientX;
  };
  const handleGalleryTouchEnd = (e) => {
    const deltaX = e.changedTouches[0].clientX - galleryTouchStartX.current;
    if (deltaX < -45) {
      handleNextMedia();
    } else if (deltaX > 45) {
      handlePrevMedia();
    }
  };

  // SEO updates
  useEffect(() => {
    if (!safeProduct?.id) return;
    document.title = `${productName || "Product"} | Chafadi`;
    const descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) {
      descriptionTag.setAttribute(
        "content",
        (productDescription || "Shop this product on Chafadi in Niger.")
          .replace(/\s+/g, " ")
          .trim()
          .slice(0, 160)
      );
    }
    const canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag) {
      canonicalTag.setAttribute(
        "href",
        `https://www.chafadi.com/products/${safeProduct.id}`
      );
    }
  }, [safeProduct?.id, productDescription, productName]);

  if (loading) return <Loader />;

  if (error) {
    return (
      <div className="max-w-xl mx-auto px-6 my-28 text-center space-y-5">
        <div className="w-16 h-16 rounded-full bg-red-500/10 text-red-500 flex items-center justify-center mx-auto">
          <FiAlertCircle size={32} />
        </div>
        <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">{error}</h2>
        <button
          onClick={() => window.location.reload()}
          className="btn-primary px-8 h-12 text-sm"
        >
          {t("common.retry", "Try Again")}
        </button>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="bg-[var(--color-bg)] text-[var(--color-text-primary)] min-h-screen pb-28 sm:pb-32">
      {/* ── 1. MOBILE TOP APP BAR (< lg) ── */}
      <div className="lg:hidden sticky top-0 z-30 bg-[var(--color-bg)]/90 backdrop-blur-xl border-b border-[var(--color-border)] px-4 py-2.5 flex items-center justify-between gap-3 shadow-xs">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] flex items-center justify-center text-[var(--color-text-primary)] active:scale-95 transition-all shrink-0"
          aria-label="Go back"
        >
          <FiArrowLeft size={16} />
        </button>

        <div className="min-w-0 text-center flex-1 px-1">
          <span className="text-xs font-semibold text-[var(--color-text-primary)] truncate block">
            {productName}
          </span>
          {safeProduct.category && (
            <span className="text-[10px] text-[var(--color-text-secondary)] truncate block">
              {productCategoryName}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={handleShare}
            aria-label="Share product"
            className="w-9 h-9 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] flex items-center justify-center active:scale-95 transition-all"
          >
            {copiedLink ? <FiCheck size={14} className="text-emerald-500" /> : <FiShare2 size={14} />}
          </button>
          <FavoriteButton
            product={safeProduct}
            size="sm"
            className="w-9 h-9 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-primary)] active:scale-95 transition-all"
          />
        </div>
      </div>

      {/* ── DESKTOP BREADCRUMBS (>= lg) ── */}
      <nav aria-label="Breadcrumb" className="hidden lg:block max-w-7xl mx-auto px-6 pt-5 pb-2">
        <ol className="flex items-center gap-2 text-[12px] font-medium text-[var(--color-text-secondary)] overflow-x-auto no-scrollbar whitespace-nowrap">
          <li>
            <Link to="/" className="hover:text-[var(--color-accent)] transition-colors">
              {t("common.home", "Home")}
            </Link>
          </li>
          <li className="text-[var(--color-border)]">
            <FiChevronRight size={13} />
          </li>
          {safeProduct.category && (
            <>
              <li>
                <Link
                  to={`/search?category=${safeProduct.category.id}`}
                  className="hover:text-[var(--color-accent)] transition-colors"
                >
                  {productCategoryName}
                </Link>
              </li>
              <li className="text-[var(--color-border)]">
                <FiChevronRight size={13} />
              </li>
            </>
          )}
          <li className="text-[var(--color-text-primary)] font-semibold truncate max-w-sm">
            {productName}
          </li>
        </ol>
      </nav>

      {/* ── 2. HERO SHOWCASE: MEDIA & PURCHASE HUB ── */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-2 sm:pt-4 lg:pt-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-14">
          
          {/* ── LEFT: STUDIO MEDIA GALLERY (Cols 1 to 7) ── */}
          <div className="lg:col-span-7 flex flex-col gap-3 sm:gap-4">
            <div className="flex flex-col lg:flex-row-reverse gap-3 sm:gap-4">
              
              {/* Main Stage Image / Video with Touch Swipe */}
              <div
                className="relative flex-1 aspect-square rounded-[24px] sm:rounded-[28px] overflow-hidden bg-[var(--color-bg-secondary)] border border-[var(--color-border)] shadow-[0_4px_32px_rgba(0,0,0,0.04)] group flex items-center justify-center transition-all select-none"
                onTouchStart={handleGalleryTouchStart}
                onTouchEnd={handleGalleryTouchEnd}
              >
                {/* Floating Badges: Stock Status */}
                <div className="absolute top-3 left-3 sm:top-4 sm:left-4 z-20 flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] sm:text-[11px] font-semibold backdrop-blur-md border ${
                      inStock
                        ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                        : "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20"
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${inStock ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
                    {inStock ? t("productDetail.inStock", "In Stock") : t("productDetail.outOfStock", "Out of Stock")}
                  </span>
                </div>

                {/* Floating Top-Right Actions on Desktop */}
                <div className="hidden lg:flex absolute top-4 right-4 z-20 items-center gap-2">
                  <button
                    type="button"
                    onClick={handleShare}
                    aria-label="Share product"
                    className="w-10 h-10 rounded-full bg-[var(--glass-bg)] backdrop-blur-md border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] flex items-center justify-center transition-transform hover:scale-105 shadow-sm"
                  >
                    {copiedLink ? <FiCheck size={16} className="text-emerald-500" /> : <FiShare2 size={16} />}
                  </button>
                  <FavoriteButton
                    product={safeProduct}
                    size="md"
                    className="w-10 h-10 rounded-full bg-[var(--glass-bg)] backdrop-blur-md border border-[var(--color-border)] text-[var(--color-text-primary)] hover:scale-105 shadow-sm"
                  />
                </div>

                {/* Left / Right Carousel Arrow Buttons */}
                {uniqueGallery.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrevMedia();
                      }}
                      className="absolute left-2.5 sm:left-4 top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-black/45 hover:bg-black/65 backdrop-blur-md text-white flex items-center justify-center transition-all active:scale-95 shadow-sm"
                      aria-label="Previous image"
                    >
                      <FiChevronLeft size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNextMedia();
                      }}
                      className="absolute right-2.5 sm:right-4 top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-black/45 hover:bg-black/65 backdrop-blur-md text-white flex items-center justify-center transition-all active:scale-95 shadow-sm"
                      aria-label="Next image"
                    >
                      <FiChevronRight size={18} />
                    </button>
                  </>
                )}

                {/* Floating Media Counter Badge */}
                {uniqueGallery.length > 1 && (
                  <div className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4 z-20 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-md text-white text-[10px] sm:text-[11px] font-medium tracking-wide">
                    {currentMediaIndex >= 0 ? currentMediaIndex + 1 : 1} / {uniqueGallery.length}
                  </div>
                )}

                {/* Media Element */}
                {selectedMediaType === "image" ? (
                  <img
                    src={selectedMedia}
                    alt={productName}
                    onClick={() => setLightboxOpen(true)}
                    className="w-full h-full object-cover cursor-zoom-in transition-transform duration-500 ease-[cubic-bezier(0.28,0.11,0.32,1)] group-hover:scale-[1.03]"
                  />
                ) : (
                  <video
                    key={selectedMedia}
                    src={selectedMedia}
                    controls
                    autoPlay
                    muted
                    loop
                    className="w-full h-full object-cover rounded-[20px]"
                  />
                )}

                {/* Zoom Lightbox Trigger */}
                {selectedMediaType === "image" && (
                  <button
                    onClick={() => setLightboxOpen(true)}
                    className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 p-2 sm:p-2.5 rounded-full bg-[var(--glass-bg)] backdrop-blur-md border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] transition-all opacity-80 hover:opacity-100 hover:scale-105 shadow-sm"
                    title="Zoom Image"
                  >
                    <FiMaximize2 size={15} />
                  </button>
                )}
              </div>

              {/* Thumbnails (Mobile: Horizontal row UNDER image; Desktop: Vertical Stack on left) */}
              {uniqueGallery.length > 1 && (
                <div className="flex lg:flex-col gap-2.5 overflow-x-auto lg:overflow-y-auto no-scrollbar lg:w-20 shrink-0 py-1 lg:py-0 max-h-[540px]">
                  {uniqueGallery.map((item) => {
                    const isSelected = selectedMedia === item.media_url;
                    return (
                      <button
                        key={item.media_url}
                        type="button"
                        onClick={() => {
                          setSelectedMedia(item.media_url);
                          setSelectedMediaType(item.media_type || "image");
                        }}
                        className={`relative w-16 h-16 sm:w-18 sm:h-18 lg:w-20 lg:h-20 aspect-square rounded-[16px] sm:rounded-[18px] overflow-hidden border-2 transition-all shrink-0 bg-[var(--color-bg-secondary)] ${
                          isSelected
                            ? "border-[var(--color-accent)] ring-2 ring-[var(--color-accent)]/20 shadow-md scale-[1.02]"
                            : "border-[var(--color-border)] hover:border-[var(--color-accent)]/60 opacity-75 hover:opacity-100"
                        }`}
                      >
                        {item.media_type === "image" ? (
                          <img
                            src={item.media_url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-black/80 rounded-xl flex items-center justify-center text-white">
                            <FiPlay size={16} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── RIGHT: PRODUCT INFO & PURCHASE HUB (Cols 8 to 12) ── */}
          <div className="lg:col-span-5 flex flex-col space-y-4 sm:space-y-5">
            
            {/* Top Meta: Views, Date Listed, ID & Staff Edit */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2 text-[12px] font-medium text-[var(--color-text-secondary)]">
                <span className="flex items-center gap-1">
                  <FiEye size={14} className="text-[var(--color-text-tertiary)]" />
                  {safeProduct.view_count || 0} {t("views", "views")}
                </span>
                <span>•</span>
                <span>ID: #{safeProduct.id}</span>
                {addedDateText && (
                  <>
                    <span>•</span>
                    <span
                      className="flex items-center gap-1.5 text-[var(--color-text-tertiary)]"
                      title={fullDateText}
                    >
                      <FiCalendar size={13} className="text-[var(--color-accent)]" />
                      <span>{addedDateText}</span>
                    </span>
                  </>
                )}
              </div>

              {(user && (user.id === seller.id || user.is_staff || user.is_superuser)) && (
                <button
                  onClick={() => navigate(`/products/${safeProduct.id}/edit`)}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[11px] font-semibold text-[var(--color-text-primary)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] transition-all shadow-sm"
                >
                  <FiEdit size={12} />
                  {t("productForm.editProduct", "Edit")}
                </button>
              )}
            </div>

            {/* Product Title */}
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-[2.25rem] font-semibold text-[var(--color-text-primary)] tracking-[-0.02em] leading-tight mb-2.5">
                {productName}
              </h1>

              {/* Rating Summary Link */}
              <div className="flex items-center gap-3">
                <StarRating rating={safeProduct.average_rating || 5} size={15} />
                <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                  {safeProduct.average_rating ? Number(safeProduct.average_rating).toFixed(1) : "5.0"}
                </span>
                <button
                  onClick={() => {
                    setActiveTab("reviews");
                    document.getElementById("content-tabs")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="text-[12px] font-medium text-[var(--color-accent)] hover:underline"
                >
                  ({reviewCount} {t("common.reviews", "reviews")})
                </button>
              </div>
            </div>

            {/* ── PRICE HERO CARD ── */}
            <div className="rounded-[22px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-4 sm:p-5 shadow-[0_2px_16px_rgba(0,0,0,0.02)]">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("productDetail.price", "Price")}
                </span>
                {minOrderQty > 1 && (
                  <span className="text-[10px] sm:text-[11px] font-semibold text-[var(--color-accent)] bg-[var(--color-accent-subtle)] px-2.5 py-0.5 rounded-full">
                    Min. Order: {minOrderQty} units
                  </span>
                )}
              </div>

              <div className="flex items-baseline gap-2.5 sm:gap-3 flex-wrap">
                <span className="text-3xl sm:text-4xl font-bold tracking-tight text-[var(--color-text-primary)]">
                  {productPrice}
                </span>
                {resolvedUnitPrice * 1.2 > 0 && (
                  <span className="text-sm sm:text-base font-medium text-[var(--color-text-tertiary)] line-through">
                    {formatPrice(resolvedUnitPrice * 1.2, currency, rates)}
                  </span>
                )}
                {resolvedUnitPrice * 1.2 > 0 && (
                  <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-0.5 rounded-full">
                    -17%
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[var(--color-border)]/60 text-[11px] text-[var(--color-text-tertiary)]">
                <span
                  className={`flex items-center gap-1.5 font-semibold ${
                    stockInfo.urgency === "out"
                      ? "text-red-500"
                      : stockInfo.urgency === "low"
                      ? "text-amber-600 dark:text-amber-400"
                      : "text-emerald-600 dark:text-emerald-400"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${stockInfo.dotColor} ${
                      stockInfo.inStock ? "animate-pulse" : ""
                    }`}
                  />
                  {stockInfo.text}
                </span>
                <span>•</span>
                <span>{t("productDetail.vatNotice", "Taxes included")}</span>
              </div>
            </div>

            {/* ── ENHANCED SELECTIONS / VARIANTS (Size, Weight, Color, Model, etc.) ── */}
            {productSelections.length > 0 && (
              <div className="space-y-4 pt-1">
                {productSelections.map((selection) => {
                  const nameLower = (selection.name || "").toLowerCase();
                  const isColor = nameLower.includes("color") || nameLower.includes("couleur");
                  const isSize = nameLower.includes("size") || nameLower.includes("taille") || nameLower.includes("pointure");
                  const isWeight = nameLower.includes("weight") || nameLower.includes("poids") || nameLower.includes("gram") || nameLower.includes("kg") || nameLower.includes("volume");
                  const currentSelected = selectedSelections[selection.name] || selection.values[0];

                  const hasOptionPrices =
                    selection.prices &&
                    Object.values(selection.prices).some(
                      (p) => p !== undefined && p !== null && p !== ""
                    );

                  return (
                    <div key={selection.name} className="space-y-2.5">
                      {/* Selection Header */}
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
                          {pickLocalizedField(selection, "name", i18n.language)}:
                        </span>
                        <span className="font-semibold text-[var(--color-text-primary)]">
                          {currentSelected}
                          {selection.prices?.[currentSelected] ? (
                            <span className="ml-1.5 text-[11px] font-bold text-[var(--color-accent)]">
                              ({formatPrice(selection.prices[currentSelected], currency, rates)})
                            </span>
                          ) : null}
                        </span>
                      </div>

                      {/* Variant Options Pills */}
                      <div className="flex flex-wrap gap-2.5">
                        {selection.values.map((value) => {
                          const isPicked = currentSelected === value;
                          const effectiveOptionPrice =
                            selection.prices?.[value] !== undefined && selection.prices?.[value] !== null && selection.prices?.[value] !== ""
                              ? Number(selection.prices[value])
                              : hasOptionPrices
                              ? baseUnitPrice
                              : null;
                          const valLower = value.toLowerCase().trim();
                          const swatchColor = isColor ? COLOR_MAP[valLower] : null;

                          // 1. Color Swatch Button
                          if (isColor) {
                            return (
                              <button
                                type="button"
                                key={`${selection.name}-${value}`}
                                onClick={() =>
                                  setSelectedSelections((prev) => ({
                                    ...prev,
                                    [selection.name]: value,
                                  }))
                                }
                                aria-pressed={isPicked}
                                className={`group h-11 px-4 rounded-full text-xs font-semibold transition-all flex items-center gap-2.5 border active:scale-95 ${
                                  isPicked
                                    ? "border-[var(--color-accent)] bg-[var(--color-accent-subtle)] text-[var(--color-accent)] ring-2 ring-[var(--color-accent)]/25 shadow-xs font-bold scale-[1.02]"
                                    : "border-[var(--color-border)] bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)] hover:border-[var(--color-accent)]/60"
                                }`}
                              >
                                {swatchColor ? (
                                  <span
                                    className={`w-4 h-4 rounded-full shrink-0 border ${
                                      valLower === "white" || valLower === "blanc"
                                        ? "border-gray-300"
                                        : "border-black/10 shadow-xs"
                                    }`}
                                    style={{ backgroundColor: swatchColor }}
                                  />
                                ) : (
                                  <span className="w-2.5 h-2.5 rounded-full bg-[var(--color-accent)] shrink-0" />
                                )}
                                <span>{value}</span>
                                {effectiveOptionPrice && (
                                  <span className="text-[10px] opacity-80 font-normal">
                                    ({formatPrice(effectiveOptionPrice, currency, rates)})
                                  </span>
                                )}
                              </button>
                            );
                          }

                          // 2. Apple Size Tile (Clean, balanced, visible gradient & contrast)
                          if (isSize) {
                            return (
                              <button
                                type="button"
                                key={`${selection.name}-${value}`}
                                onClick={() =>
                                  setSelectedSelections((prev) => ({
                                    ...prev,
                                    [selection.name]: value,
                                  }))
                                }
                                aria-pressed={isPicked}
                                style={
                                  isPicked
                                    ? { background: "linear-gradient(135deg, #ff9f0a 0%, #ff5e00 100%)" }
                                    : undefined
                                }
                                className={`min-w-[74px] min-h-[50px] px-4 py-2 rounded-[14px] text-xs transition-all flex flex-col items-center justify-center border text-center active:scale-95 ${
                                  isPicked
                                    ? "bg-[var(--color-accent)] text-white border-transparent shadow-md shadow-orange-500/25 ring-2 ring-[var(--color-accent)]/30 font-bold scale-[1.02]"
                                    : "bg-[var(--color-bg-secondary)] border-[var(--color-border)] text-[var(--color-text-primary)] hover:border-[var(--color-accent)]/60 font-semibold"
                                }`}
                              >
                                <span className={`text-xs font-bold leading-tight ${isPicked ? "text-white" : "text-[var(--color-text-primary)]"}`}>
                                  {value}
                                </span>
                                {effectiveOptionPrice ? (
                                  <span
                                    className={`text-[10px] leading-tight mt-1 font-semibold ${
                                      isPicked ? "text-white/95" : "text-[var(--color-accent)]"
                                    }`}
                                  >
                                    {formatPrice(effectiveOptionPrice, currency, rates)}
                                  </span>
                                ) : null}
                              </button>
                            );
                          }

                          // 3. Weight / Model / Standard Pill
                          return (
                            <button
                              type="button"
                              key={`${selection.name}-${value}`}
                              onClick={() =>
                                setSelectedSelections((prev) => ({
                                  ...prev,
                                  [selection.name]: value,
                                }))
                              }
                              aria-pressed={isPicked}
                              style={
                                isPicked
                                  ? { background: "linear-gradient(135deg, #ff9f0a 0%, #ff5e00 100%)" }
                                  : undefined
                              }
                              className={`h-11 px-4 rounded-full text-xs font-semibold transition-all flex items-center gap-1.5 border active:scale-95 ${
                                isPicked
                                  ? "bg-[var(--color-accent)] text-white border-transparent shadow-md shadow-orange-500/25 ring-2 ring-[var(--color-accent)]/20 font-bold scale-[1.02]"
                                  : "bg-[var(--color-bg-secondary)] border-[var(--color-border)] text-[var(--color-text-primary)] hover:border-[var(--color-accent)]/60"
                              }`}
                            >
                              {isWeight && (
                                <FiTag
                                  size={12}
                                  className={isPicked ? "text-white" : "text-[var(--color-text-secondary)]"}
                                />
                              )}
                              <span>{value}</span>
                              {effectiveOptionPrice && (
                                <span className={`text-[10px] ${isPicked ? "text-white/90" : "text-[var(--color-accent)] font-medium"}`}>
                                  • {formatPrice(effectiveOptionPrice, currency, rates)}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── QUANTITY STEPPER ── */}
            <div className="space-y-2 pt-1">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
                  {t("common.quantity", "Quantity")}
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${stockInfo.badgeColor}`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${stockInfo.dotColor} ${
                      stockInfo.inStock ? "animate-pulse" : ""
                    }`}
                  />
                  {stockInfo.text}
                </span>
              </div>
              <div className="flex items-center h-12 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-full overflow-hidden w-40 p-1">
                <button
                  type="button"
                  onClick={() =>
                    setSelectedOrderSize((p) => Math.max(minOrderQty, p - 1))
                  }
                  disabled={selectedOrderSize <= minOrderQty}
                  className="w-10 h-10 rounded-full flex items-center justify-center text-[var(--color-text-primary)] hover:bg-[var(--color-bg)] transition-colors disabled:opacity-40"
                  aria-label="Decrease quantity"
                >
                  <FiMinus size={15} />
                </button>
                <div className="flex-1 h-full flex items-center justify-center font-semibold text-sm text-[var(--color-text-primary)]">
                  {selectedOrderSize}
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedOrderSize((p) => p + 1)}
                  className="w-10 h-10 rounded-full flex items-center justify-center text-[var(--color-text-primary)] hover:bg-[var(--color-bg)] transition-colors"
                  aria-label="Increase quantity"
                >
                  <FiPlus size={15} />
                </button>
              </div>
            </div>

            {/* ── PRIMARY CALL TO ACTION BUTTONS ── */}
            <div className="flex flex-col gap-3 pt-2">
              <button
                type="button"
                onClick={handleBuyNow}
                disabled={!inStock}
                className="btn-primary w-full h-12 text-[15px] font-semibold gap-2 shadow-lg shadow-orange-500/25"
              >
                <FiZap size={18} />
                {t("productDetail.buyNow", "Buy Now")}
              </button>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={handleAddToCart}
                  disabled={!inStock}
                  className="btn-secondary w-full h-12 text-[14px] font-semibold gap-2 border-[var(--color-accent)]"
                >
                  <FiShoppingBag size={16} />
                  {t("productCard.addToCart", "Add to Cart")}
                </button>

                <button
                  type="button"
                  onClick={handleSendMessage}
                  disabled={!seller.id || (user && user.id === seller.id)}
                  className="h-12 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] transition-all text-[14px] font-semibold flex items-center justify-center gap-2"
                >
                  <FiMessageCircle size={16} />
                  {t("productDetail.chatSupplier", "Chat Supplier")}
                </button>
              </div>
            </div>

            {/* ── SUPPLIER TRUST MINI-CARD ── */}
            <div className="rounded-[22px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] p-4 flex items-center justify-between gap-4 mt-1">
              <div className="flex items-center gap-3.5 min-w-0">
                <div className="relative shrink-0">
                  <img
                    src={
                      sellerProfile?.logo_url ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        sellerProfile?.company_name || seller.first_name || "Supplier"
                      )}&background=ff6a00&color=fff&bold=true`
                    }
                    alt=""
                    className="w-12 h-12 rounded-full object-cover border-2 border-[var(--color-bg)] shadow-sm"
                  />
                  {sellerProfile?.is_verified && (
                    <div className="absolute -bottom-1 -right-1">
                      <VerifiedSellerBadge isVerified={true} size={18} />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h4 className="text-sm font-semibold text-[var(--color-text-primary)] truncate">
                      {sellerProfile?.company_name || `${seller.first_name || ""} ${seller.last_name || ""}` || "Chafadi Merchant"}
                    </h4>
                  </div>
                  <p className="text-[11px] text-[var(--color-text-secondary)] truncate">
                    📍 {sellerProfile?.city || "Niamey, Niger"} • {sellerProfile?.is_verified ? "Verified Supplier" : "Active Merchant"}
                  </p>
                </div>
              </div>

              <Link
                to={seller.id ? `/users/${seller.id}` : "#"}
                className="px-4 py-2 rounded-full bg-[var(--color-bg)] border border-[var(--color-border)] text-xs font-semibold text-[var(--color-text-primary)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] transition-all shrink-0 no-underline"
              >
                {t("productDetail.viewStore", "Store")}
              </Link>
            </div>

            {/* ── 4 TRUST & VALUE PROPOSITION BADGES ── */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3.5 pt-2">
              <div className="flex items-center gap-3 p-3 rounded-[18px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)]">
                <div className="w-8 h-8 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                  <FiTruck size={16} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-[var(--color-text-primary)] leading-tight">
                    Fast Delivery
                  </p>
                  <p className="text-[10px] text-[var(--color-text-secondary)] mt-0.5 leading-tight">
                    Across Niger
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-[18px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)]">
                <div className="w-8 h-8 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
                  <FiShield size={16} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-[var(--color-text-primary)] leading-tight">
                    Escrow Protection
                  </p>
                  <p className="text-[10px] text-[var(--color-text-secondary)] mt-0.5 leading-tight">
                    100% Safe Pay
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-[18px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)]">
                <div className="w-8 h-8 rounded-full bg-orange-500/10 text-[var(--color-accent)] flex items-center justify-center shrink-0">
                  <FiAward size={16} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-[var(--color-text-primary)] leading-tight">
                    Verified Sellers
                  </p>
                  <p className="text-[10px] text-[var(--color-text-secondary)] mt-0.5 leading-tight">
                    Direct Trade
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-[18px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)]">
                <div className="w-8 h-8 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                  <FiMessageCircle size={16} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-[var(--color-text-primary)] leading-tight">
                    Instant Chat
                  </p>
                  <p className="text-[10px] text-[var(--color-text-secondary)] mt-0.5 leading-tight">
                    Negotiate Live
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── 3. SEGMENTED TABS SECTION (Tightened Spacing, Unified Review Hub) ── */}
        <div id="content-tabs" className="mt-8 sm:mt-12 pt-6 border-t border-[var(--color-border)]">
          {/* Segmented Pill Tabs Header */}
          <div className="flex items-center justify-center sm:justify-start">
            <div className="inline-flex p-1 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] overflow-x-auto no-scrollbar gap-1 max-w-full">
              {[
                { id: "description", label: t("productDetail.overview", "Overview") },
                { id: "specifications", label: t("productDetail.specifications", "Specs") },
                {
                  id: "reviews",
                  label: `${t("common.reviews", "Reviews")} (${reviewCount})`,
                },
                { id: "supplier", label: t("productDetail.supplierInfo", "Supplier") },
              ].map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-3.5 sm:px-5 py-1.5 sm:py-2 rounded-full text-[11px] sm:text-xs font-semibold transition-all whitespace-nowrap ${
                      isActive
                        ? "bg-[var(--color-bg)] text-[var(--color-text-primary)] shadow-sm border border-[var(--color-border)]"
                        : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content Panes (Zero forced empty minimum heights) */}
          <div className="mt-6">
            {/* TAB: DESCRIPTION / OVERVIEW */}
            {activeTab === "description" && (
              <div className="max-w-4xl space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base sm:text-xl font-semibold text-[var(--color-text-primary)] tracking-tight">
                    {t("productDetail.descriptionTitle", "Product Story & Features")}
                  </h3>
                  <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    <FiCheckCircle size={13} /> Verified Listing
                  </span>
                </div>

                {productDescription ? (
                  <div className="text-xs sm:text-sm font-normal leading-relaxed text-[var(--color-text-secondary)] whitespace-pre-line space-y-3 bg-[var(--color-bg-secondary)] p-4 sm:p-5 rounded-[20px] border border-[var(--color-border)]">
                    {productDescription}
                  </div>
                ) : (
                  <div className="p-4 sm:p-5 rounded-[20px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] space-y-3.5">
                    <p className="text-xs sm:text-sm text-[var(--color-text-secondary)] leading-relaxed">
                      {productName} is sourced directly by{" "}
                      <span className="font-semibold text-[var(--color-text-primary)]">
                        {sellerProfile?.company_name || `${seller.first_name || ""} ${seller.last_name || ""}`.trim() || "Verified Merchant"}
                      </span>{" "}
                      and available for secure dispatch across Niger.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-1">
                      <div className="p-3 rounded-[14px] bg-[var(--color-bg)] border border-[var(--color-border)] text-left">
                        <span className="text-[10px] font-bold text-[var(--color-text-tertiary)] uppercase block">Category</span>
                        <span className="text-xs font-semibold text-[var(--color-text-primary)] truncate block mt-0.5">
                          {productCategoryName || "General Merchandise"}
                        </span>
                      </div>
                      <div className="p-3 rounded-[14px] bg-[var(--color-bg)] border border-[var(--color-border)] text-left">
                        <span className="text-[10px] font-bold text-[var(--color-text-tertiary)] uppercase block">Dispatched From</span>
                        <span className="text-xs font-semibold text-[var(--color-text-primary)] truncate block mt-0.5">
                          📍 {sellerProfile?.city || "Niamey, Niger"}
                        </span>
                      </div>
                      <div className="p-3 rounded-[14px] bg-[var(--color-bg)] border border-[var(--color-border)] text-left">
                        <span className="text-[10px] font-bold text-[var(--color-text-tertiary)] uppercase block">Buyer Security</span>
                        <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 truncate block mt-0.5">
                          100% Escrow Protected
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {productExtraInformation && (
                  <div className="mt-4 p-5 rounded-[20px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)]">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--color-accent)] mb-2">
                      {t("productDetail.additionalHighlights", "Additional Highlights")}
                    </h4>
                    <p className="text-xs sm:text-sm font-medium text-[var(--color-text-primary)] whitespace-pre-line">
                      {productExtraInformation}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* TAB: SPECIFICATIONS */}
            {activeTab === "specifications" && (
              <div className="max-w-3xl">
                <h3 className="text-lg sm:text-xl font-semibold text-[var(--color-text-primary)] tracking-tight mb-4">
                  {t("productDetail.techSpecs", "Technical Specifications")}
                </h3>
                <div className="rounded-[20px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] overflow-hidden">
                  <table className="w-full text-left text-xs sm:text-sm">
                    <tbody>
                      <tr className="border-b border-[var(--color-border)]">
                        <td className="py-3.5 px-5 font-semibold text-[var(--color-text-secondary)] w-1/3">
                          {t("productDetail.productCategory", "Category")}
                        </td>
                        <td className="py-3.5 px-5 font-medium text-[var(--color-text-primary)]">
                          {productCategoryName || "General Merchandise"}
                        </td>
                      </tr>
                      <tr className="border-b border-[var(--color-border)]">
                        <td className="py-3.5 px-5 font-semibold text-[var(--color-text-secondary)]">
                          {t("productDetail.minOrder", "Minimum Order Quantity")}
                        </td>
                        <td className="py-3.5 px-5 font-medium text-[var(--color-text-primary)]">
                          {minOrderQty} {minOrderQty === 1 ? "unit" : "units"}
                        </td>
                      </tr>
                      <tr className="border-b border-[var(--color-border)]">
                        <td className="py-3.5 px-5 font-semibold text-[var(--color-text-secondary)]">
                          {t("productDetail.stockAvailability", "Stock Availability")}
                        </td>
                        <td className="py-3.5 px-5 font-medium text-[var(--color-text-primary)]">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${stockInfo.badgeColor}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${stockInfo.dotColor}`} />
                            {stockInfo.text}
                          </span>
                        </td>
                      </tr>
                      {safeProduct.created_at && (
                        <tr className="border-b border-[var(--color-border)]">
                          <td className="py-3.5 px-5 font-semibold text-[var(--color-text-secondary)]">
                            {t("productDetail.dateListed", "Date Listed")}
                          </td>
                          <td className="py-3.5 px-5 font-medium text-[var(--color-text-primary)]">
                            {fullDateText} {addedDateText ? `(${addedDateText})` : ""}
                          </td>
                        </tr>
                      )}
                      {productSelections.map((sel) => (
                        <tr key={sel.name} className="border-b border-[var(--color-border)]">
                          <td className="py-3.5 px-5 font-semibold text-[var(--color-text-secondary)]">
                            {pickLocalizedField(sel, "name", i18n.language)}
                          </td>
                          <td className="py-3.5 px-5 font-medium text-[var(--color-text-primary)]">
                            {sel.values.join(", ")}
                          </td>
                        </tr>
                      ))}
                      {productExtraInformation && (
                        <tr>
                          <td className="py-3.5 px-5 font-semibold text-[var(--color-text-secondary)]">
                            {t("productDetail.extraInfo", "Additional Notes")}
                          </td>
                          <td className="py-3.5 px-5 font-medium text-[var(--color-text-primary)]">
                            {productExtraInformation}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB: REVIEWS (Unified Single Apple Review Hub) */}
            {activeTab === "reviews" && (
              <div className="max-w-4xl space-y-6">
                <div className="p-6 sm:p-7 rounded-[24px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] shadow-sm">
                  {/* Rating Header & Actions */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-6 border-b border-[var(--color-border)]">
                    <div className="flex items-center gap-4">
                      <div className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[var(--color-text-primary)]">
                        {safeProduct.average_rating ? Number(safeProduct.average_rating).toFixed(1) : "5.0"}
                      </div>
                      <div>
                        <StarRating rating={safeProduct.average_rating || 5} size={16} />
                        <p className="text-xs font-medium text-[var(--color-text-secondary)] mt-1">
                          {reviewCount} {t("productDetail.verifiedRatings", "Verified Ratings")}
                        </p>
                      </div>
                    </div>

                    {/* Action button inside header */}
                    <div>
                      {user && user.id !== seller.id && !userHasReviewed ? (
                        <button
                          type="button"
                          onClick={() => setShowReviewForm((prev) => !prev)}
                          className="btn-primary h-10 px-5 text-xs font-semibold gap-1.5 shadow-sm"
                        >
                          <FiEdit size={14} />
                          {showReviewForm ? t("common.cancel", "Cancel") : t("productDetail.writeReview", "Write a Review")}
                        </button>
                      ) : user && user.id !== seller.id && userHasReviewed ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-full border border-emerald-500/20">
                          <FiCheckCircle size={14} /> {t("productDetail.alreadyReviewed", "Review submitted")}
                        </span>
                      ) : !user ? (
                        <div className="flex items-center gap-2.5">
                          <span className="text-xs text-[var(--color-text-secondary)] hidden sm:inline">Purchased this item?</span>
                          <Link
                            to="/login"
                            className="btn-secondary h-9 px-4 text-xs font-semibold"
                          >
                            {t("auth.login", "Sign In to Review")}
                          </Link>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Expandable Review Submission Form */}
                  {showReviewForm && user && user.id !== seller.id && !userHasReviewed && (
                    <div className="pt-5">
                      <ReviewForm
                        productId={id}
                        onReviewSubmitted={() => {
                          setShowReviewForm(false);
                          fetchProductData();
                        }}
                      />
                    </div>
                  )}

                  {/* Reviews List OR Clean Empty State */}
                  <div className="pt-5">
                    {reviews.length > 0 ? (
                      <div className="space-y-3">
                        {reviews.map((rev) => (
                          <ReviewItem
                            key={rev.id}
                            review={{ ...rev, product: id }}
                            onReviewUpdated={fetchProductData}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-xs sm:text-sm text-[var(--color-text-secondary)] font-medium">
                        <FiMessageCircle size={24} className="text-[var(--color-text-tertiary)] mx-auto mb-2 opacity-60" />
                        <p className="font-semibold text-[var(--color-text-primary)]">
                          {t("productDetail.noReviewsYet", "No customer reviews yet")}
                        </p>
                        <p className="text-[11px] text-[var(--color-text-tertiary)] mt-1">
                          Be the first buyer to review this product and help others make informed choices.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* TAB: SUPPLIER & POLICIES */}
            {activeTab === "supplier" && (
              <div className="max-w-4xl space-y-5">
                <div className="p-5 sm:p-6 rounded-[22px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
                  <div className="flex items-center gap-4">
                    <img
                      src={
                        sellerProfile?.logo_url ||
                        `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          sellerProfile?.company_name || seller.first_name || "Supplier"
                        )}&background=ff6a00&color=fff&bold=true`
                      }
                      alt=""
                      className="w-14 h-14 rounded-full object-cover border-2 border-[var(--color-bg)] shadow-sm shrink-0"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-base font-semibold text-[var(--color-text-primary)]">
                          {sellerProfile?.company_name || `${seller.first_name || ""} ${seller.last_name || ""}`}
                        </h4>
                        {sellerProfile?.is_verified && <VerifiedSellerBadge isVerified={true} size={18} />}
                      </div>
                      <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">
                        📍 {sellerProfile?.address || sellerProfile?.city || "Niamey, Niger"} • Member since{" "}
                        {seller.date_joined ? new Date(seller.date_joined).getFullYear() : "2024"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5 w-full sm:w-auto">
                    <button
                      onClick={handleSendMessage}
                      className="btn-primary flex-1 sm:flex-initial h-10 px-4 text-xs font-semibold gap-1.5"
                    >
                      <FiMessageCircle size={14} />
                      {t("productDetail.messageSupplier", "Send Message")}
                    </button>
                    <Link
                      to={seller.id ? `/users/${seller.id}` : "#"}
                      className="btn-secondary flex-1 sm:flex-initial h-10 px-4 text-xs font-semibold text-center"
                    >
                      {t("productDetail.visitStore", "Visit Store")}
                    </Link>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div className="p-5 rounded-[20px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] space-y-1.5">
                    <div className="flex items-center gap-2 text-[var(--color-accent)] font-semibold text-xs sm:text-sm">
                      <FiTruck size={16} />
                      <h5>Shipping & Fulfillment</h5>
                    </div>
                    <p className="text-xs text-[var(--color-text-secondary)] leading-relaxed">
                      Orders are fulfilled directly by the seller or through Chafadi verified logistics partners. Same-day delivery available within Niamey.
                    </p>
                  </div>

                  <div className="p-5 rounded-[20px] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] space-y-1.5">
                    <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-semibold text-xs sm:text-sm">
                      <FiShield size={16} />
                      <h5>Escrow Protection</h5>
                    </div>
                    <p className="text-xs text-[var(--color-text-secondary)] leading-relaxed">
                      Your payment is held in escrow and released to the supplier only after you have received and inspected the delivered goods.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── 4. RECOMMENDED & RELATED PRODUCTS (Compact Carousel Slider) ── */}
        {relatedProducts.length > 0 && (
          <section className="mt-8 sm:mt-10 pt-6 border-t border-[var(--color-border)]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold tracking-tight text-[var(--color-text-primary)]">
                  {t("productDetail.relatedTitle", "You May Also Like")}
                </h3>
                <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">
                  More products from {productCategoryName || "Chafadi"}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to={safeProduct.category ? `/search?category=${safeProduct.category.id}` : "/search"}
                  className="text-xs font-semibold text-[var(--color-accent)] hover:underline flex items-center gap-1"
                >
                  {t("common.seeAll", "See All")} <FiChevronRight size={14} />
                </Link>
                {/* Desktop Carousel Arrows */}
                <div className="hidden sm:flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => scrollRelated("left")}
                    className="w-8 h-8 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-secondary)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] flex items-center justify-center transition-all"
                    aria-label="Scroll left"
                  >
                    <FiChevronLeft size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollRelated("right")}
                    className="w-8 h-8 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-secondary)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] flex items-center justify-center transition-all"
                    aria-label="Scroll right"
                  >
                    <FiChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Carousel Container with compact card widths */}
            <div
              ref={relatedScrollRef}
              className="flex gap-3 sm:gap-4 overflow-x-auto no-scrollbar scroll-smooth py-2 px-1"
            >
              {relatedProducts.slice(0, 12).map((relProduct) => (
                <div
                  key={relProduct.id}
                  className="w-[155px] sm:w-[170px] md:w-[185px] shrink-0"
                >
                  <ProductCard product={relProduct} />
                </div>
              ))}
            </div>
          </section>
        )}
      </main>



      {/* ── 6. LIGHTBOX MODAL ── */}
      {lightboxOpen && selectedMediaType === "image" && (
        <div
          className="fixed inset-0 z-[1000] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4 transition-opacity animate-in fade-in"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center transition-all z-20"
            aria-label="Close lightbox"
          >
            <FiX size={24} />
          </button>
          <img
            src={selectedMedia}
            alt={productName}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[88vh] object-contain rounded-2xl shadow-2xl transition-transform"
          />
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
